import { useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, Loader2, X } from "lucide-react";
import { CARD_SIZES, CATEGORY_LABEL } from "@/lib/card";
import { uploadImage, errMsg } from "@/lib/api";
import { Group, Row, Num, Color, Select } from "./fields";
import { Switch } from "@/components/ui/switch";

const SWATCHES = [
  "#FFFFFF", "#0F172A", "#F8FAFC", "#FEF3C7",
  "linear-gradient(135deg,#FFFBEB 0%,#FEF3C7 50%,#FFFBEB 100%)",
  "linear-gradient(135deg,#111827 0%,#1F2937 55%,#0B0F19 100%)",
  "linear-gradient(135deg,#0EA5E9 0%,#6366F1 100%)",
  "linear-gradient(135deg,#B8860B 0%,#FDE68A 50%,#B8860B 100%)",
  "linear-gradient(160deg,#064E3B 0%,#10B981 100%)",
  "linear-gradient(135deg,#7F1D1D 0%,#DC2626 100%)",
];

export default function CardSettings({ tpl, side, commit }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const s = tpl[side];
  const setSide = (patch) => commit((t) => ({ ...t, [side]: { ...t[side], ...patch } }));
  const set = (patch) => commit((t) => ({ ...t, ...patch }));
  const sizeId = CARD_SIZES.find((c) => c.w === tpl.width && c.h === tpl.height)?.id || "custom";

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setBusy(true);
    try { setSide({ bgImage: await uploadImage(f) }); } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  return (
    <div className="p-4 space-y-5" data-testid="card-settings">
      <div>
        <p className="font-heading font-bold text-sm">Card settings</p>
        <p className="text-[11px] text-slate-500 mt-0.5">Select an element on the canvas to edit it.</p>
      </div>
      <Group title="Card size">
        <Select value={sizeId} testId="card-size-select"
          onChange={(id) => { const c = CARD_SIZES.find((x) => x.id === id); if (c) set({ width: c.w, height: c.h }); }}
          options={[...CARD_SIZES.map((c) => ({ value: c.id, label: c.label })), { value: "custom", label: "Custom" }]} />
        <div className="grid grid-cols-2 gap-2">
          <Row label="Width"><Num value={tpl.width} min={10} suffix="mm" onChange={(width) => set({ width })} testId="card-width-input" /></Row>
          <Row label="Height"><Num value={tpl.height} min={10} suffix="mm" onChange={(height) => set({ height })} testId="card-height-input" /></Row>
        </div>
        <Row label="Category">
          <Select value={tpl.category} onChange={(category) => set({ category })} testId="card-category-select"
            options={Object.entries(CATEGORY_LABEL).map(([value, label]) => ({ value, label }))} />
        </Row>
        <label className="flex items-center justify-between text-[11px] text-slate-400">
          Double-sided (print back)
          <Switch checked={tpl.double_sided ?? true} onCheckedChange={(v) => set({ double_sided: v })} data-testid="double-sided-switch" />
        </label>
        <label className="flex items-center justify-between text-[11px] text-slate-400">
          Public verify page (QR scan)
          <Switch checked={tpl.public_verify ?? tpl.category === "jewellery"} onCheckedChange={(v) => set({ public_verify: v })} data-testid="public-verify-switch" />
        </label>
      </Group>
      <Group title={`${side} background`}>
        <Row label="Color"><Color value={s.background} onChange={(background) => setSide({ background })} testId="side-background-color" /></Row>
        <div className="grid grid-cols-5 gap-1.5">
          {SWATCHES.map((b, i) => (
            <button key={b} onClick={() => setSide({ background: b })} data-testid={`bg-swatch-${i}`}
              className={`h-8 rounded-md border ${s.background === b ? "border-amber-400" : "border-slate-700"} hover:scale-105 transition-transform`} style={{ background: b }} />
          ))}
        </div>
        <div className="flex gap-2">
          <button onClick={() => ref.current?.click()} className="cf-btn-ghost h-8 text-xs flex-1" data-testid="bg-image-upload-button">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} {s.bgImage ? "Replace" : "Background image"}
          </button>
          {s.bgImage && <button onClick={() => setSide({ bgImage: "" })} className="cf-btn-ghost h-8 px-2" data-testid="bg-image-remove-button"><X size={14} /></button>}
        </div>
        <input ref={ref} type="file" accept="image/*" className="hidden" onChange={onFile} data-testid="bg-image-file-input" />
      </Group>
      <Group title="Print notes">
        <ul className="text-[11px] text-slate-400 space-y-1.5 leading-relaxed list-disc pl-4">
          <li>Blue dashed line = 3 mm safe zone. Keep text inside it.</li>
          <li>CR80 is the standard PVC card size for Evolis, Zebra, Fargo & Datacard printers.</li>
          <li>Fields like <span className="font-mono text-indigo-300">{"{{NAME}}"}</span> are filled from your records.</li>
        </ul>
      </Group>
    </div>
  );
}
